// src/components/JobSearchMetrics.tsx

import React, { useState, useEffect } from 'react';
import { useDarkMode } from '../context/DarkModeContext';
import { JobApplicationEnhanced, ApplicationStatus } from '../types/jobSearch';
import { calculateApplicationMetrics, statusConfig } from '../utils/jobApplicationUtils';
import {
    IoTrendingUpOutline,
    IoTimeOutline,
    IoCheckmarkCircleOutline,
    IoCloseCircleOutline,
    IoBarChartOutline,
    IoChevronDownOutline,
    IoChevronUpOutline
} from 'react-icons/io5';

interface JobSearchMetricsProps {
    applications: JobApplicationEnhanced[];
    timeRange?: 'week' | 'month' | 'quarter' | 'year' | 'all';
}

const JobSearchMetrics: React.FC<JobSearchMetricsProps> = ({
    applications,
    timeRange = 'all'
}) => {
    const { darkMode } = useDarkMode();
    const metrics = calculateApplicationMetrics(applications);
    const [isExpanded, setIsExpanded] = useState(() => {
        const saved = localStorage.getItem('jobSearchMetrics.isExpanded');
        return saved ? JSON.parse(saved) : false; // Default to collapsed
    });

    useEffect(() => {
        localStorage.setItem('jobSearchMetrics.isExpanded', JSON.stringify(isExpanded));
    }, [isExpanded]);

    const MetricCard: React.FC<{
        title: string;
        value: string | number;
        icon: React.ReactNode;
        trend?: number;
    }> = ({ title, value, icon, trend }) => (
        <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow`}>
            <div className="flex justify-between items-start mb-2">
                <span className="text-sm font-medium text-gray-500">{title}</span>
                {icon}
            </div>
            <div className="flex items-end justify-between">
                <div className="text-2xl font-bold">
                    {typeof value === 'number' && isNaN(value) ? '0' : value}
                </div>
                {trend !== undefined && !isNaN(trend) && (
                    <div className={`flex items-center text-sm ${trend >= 0 ? 'text-green-500' : 'text-red-500'
                        }`}>
                        <IoTrendingUpOutline
                            className={`mr-1 ${trend < 0 ? 'transform rotate-180' : ''}`}
                        />
                        {Math.abs(trend)}%
                    </div>
                )}
            </div>
        </div>
    );

    const StatusDistributionChart: React.FC = () => {
        if (metrics.totalApplications === 0) {
            return (
                <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow`}>
                    <h3 className="text-lg font-semibold mb-4">Status Distribution</h3>
                    <p className="text-center text-gray-500">No data available</p>
                </div>
            );
        }

        return (
            <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow`}>
                <h3 className="text-lg font-semibold mb-4">Status Distribution</h3>
                <div className="space-y-3">
                    {Object.entries(metrics.statusDistribution).map(([status, count]) => {
                        const percentage = (count / metrics.totalApplications) * 100;
                        const config = statusConfig[status as ApplicationStatus];

                        return (
                            <div key={status} className="space-y-1">
                                <div className="flex justify-between text-sm">
                                    <span className="flex items-center">
                                        <span className="mr-2">{config.icon}</span>
                                        {config.label}
                                    </span>
                                    <span>{count}</span>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-2">
                                    <div
                                        className={`${config.color} rounded-full h-2`}
                                        style={{ width: `${percentage}%` }}
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    const TimelineMetrics: React.FC = () => (
        <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow`}>
            <h3 className="text-lg font-semibold mb-4">Timeline Metrics</h3>
            <div className="space-y-4">
                <div className="flex justify-between items-center">
                    <span>Average Time to Offer</span>
                    <span className="font-semibold">
                        {metrics.averageTimeToOffer.toFixed(1)} days
                    </span>
                </div>
                <div className="flex justify-between items-center">
                    <span>Interview Rate</span>
                    <span className="font-semibold">
                        {metrics.interviewRate.toFixed(1)}%
                    </span>
                </div>
                <div className="flex justify-between items-center">
                    <span>Offer Rate</span>
                    <span className="font-semibold">
                        {metrics.offerRate.toFixed(1)}%
                    </span>
                </div>
            </div>
        </div>
    );

    return (
        <div className={`rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow`}>
            <div 
                className="p-4 flex justify-between items-center cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700"
                onClick={() => setIsExpanded(!isExpanded)}
            >
                <h2 className="text-lg font-semibold">Application Metrics</h2>
                <button className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600">
                    {isExpanded ? <IoChevronUpOutline size={20} /> : <IoChevronDownOutline size={20} />}
                </button>
            </div>

            {isExpanded && (
                <div className="p-4 border-t">
                    {applications.length === 0 ? (
                        <div className="text-center py-8">
                            <p className="text-gray-500">No applications data available</p>
                            <p className="text-sm text-gray-400 mt-2">Add some job applications to see metrics</p>
                        </div>
                    ) : (
                        <div className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                <MetricCard
                                    title="Total Applications"
                                    value={metrics.totalApplications}
                                    icon={<IoBarChartOutline className="text-blue-500" size={24} />}
                                />
                                <MetricCard
                                    title="Active Applications"
                                    value={metrics.activeApplications}
                                    icon={<IoTimeOutline className="text-yellow-500" size={24} />}
                                />
                                <MetricCard
                                    title="Success Rate"
                                    value={`${metrics.offerRate.toFixed(1)}%`}
                                    icon={<IoCheckmarkCircleOutline className="text-green-500" size={24} />}
                                />
                                <MetricCard
                                    title="Rejection Rate"
                                    value={`${((metrics.statusDistribution.denied || 0) / metrics.totalApplications * 100).toFixed(1)}%`}
                                    icon={<IoCloseCircleOutline className="text-red-500" size={24} />}
                                />
                            </div>

                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                <StatusDistributionChart />
                                <TimelineMetrics />
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default JobSearchMetrics;
